import React, { useMemo, useState, useEffect } from "react";
import { Modal } from "antd";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import ReactPlayer from "react-player";
import { MediaType } from "constants/field-values";
import PDFViewer from "components/pdf-viewer/pdf-viewer";

interface MediaPlayerSibling {
  src: string;
  mediaType: MediaType;
}

interface MediaPlayerProps {
  loading?: boolean;
  onCancel: () => void;
  src: string;
  open: boolean;
  mediaType: MediaType | null;
  siblings?: MediaPlayerSibling[];
  siblingIndex?: number;
}

const MediaPlayer: React.FC<MediaPlayerProps> = (props: MediaPlayerProps) => {
  const { loading, onCancel, src, open, mediaType, siblings, siblingIndex } = props;

  const hasCarousel = siblings && siblings.length > 1;
  const [currentIndex, setCurrentIndex] = useState(siblingIndex ?? 0);

  useEffect(() => {
    if (open) {
      setCurrentIndex(siblingIndex ?? 0);
    }
  }, [open, siblingIndex]);

  const activeSrc = hasCarousel ? siblings![currentIndex].src : src;
  const activeMediaType = hasCarousel ? siblings![currentIndex].mediaType : mediaType;

  const goPrev = () => setCurrentIndex((i) => (i - 1 + siblings!.length) % siblings!.length);
  const goNext = () => setCurrentIndex((i) => (i + 1) % siblings!.length);

  const mediaContent = useMemo(() => {
    if (open && activeSrc) {
      switch (activeMediaType) {
        case MediaType.Audio:
          return (
            <ReactPlayer
              className={"audio-player"}
              url={activeSrc}
              controls
              height={"100%"}
              width={"100%"}
            />
          );

        case MediaType.Video:
          return (
            <ReactPlayer
              className={"video-player"}
              url={activeSrc}
              controls
              loop
              height={"100%"}
              width={"100%"}
            />
          );

        case MediaType.Image:
          return <img className={"img-player"} src={activeSrc} alt="Media preview" />;

        default:
          return null;
      }
    }
    return null;
  }, [open, activeMediaType, activeSrc]);

  if (activeMediaType === MediaType.Document && open && activeSrc) {
    return (
      <div className="media-player-pdf-wrapper">
        <PDFViewer pdfUrl={activeSrc} onCancel={onCancel} />
        {hasCarousel && (
          <>
            <button
              onClick={goPrev}
              className="media-player-nav-btn media-player-nav-btn--pdf media-player-nav-btn--pdf-prev"
              aria-label="Previous"
            >
              <LeftOutlined />
            </button>
            <button
              onClick={goNext}
              className="media-player-nav-btn media-player-nav-btn--pdf media-player-nav-btn--pdf-next"
              aria-label="Next"
            >
              <RightOutlined />
            </button>
            <div className="media-player-counter media-player-counter--pdf">
              {currentIndex + 1} / {siblings!.length}
            </div>
          </>
        )}
      </div>
    );
  }

  return (
    <Modal
      loading={loading}
      open={open}
      onCancel={onCancel}
      footer={null}
      className="video-player-modal"
      centered
      width="90%"
      styles={{
        body: {
          padding: 0,
          height: "calc(80vh - 40px)",
          overflow: "hidden",
        },
      }}
    >
      <div className="player-container">
        {mediaContent}
        {hasCarousel && (
          <>
            <button
              onClick={goPrev}
              className="media-player-nav-btn media-player-nav-btn--modal media-player-nav-btn--modal-prev"
              aria-label="Previous"
            >
              <LeftOutlined />
            </button>
            <button
              onClick={goNext}
              className="media-player-nav-btn media-player-nav-btn--modal media-player-nav-btn--modal-next"
              aria-label="Next"
            >
              <RightOutlined />
            </button>
            <div className="media-player-counter media-player-counter--modal">
              {currentIndex + 1} / {siblings!.length}
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

export default MediaPlayer;
